import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Button,
  Typography,
  Grid,
  Chip,
  Paper,
  Stack
} from '@mui/material';
import { Person, Lock, CreditCard, ExitToApp } from '@mui/icons-material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { api } from '../utils/api';
import { toast } from 'react-toastify';

const AccountPage = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileForm, setProfileForm] = useState({ name: '', email: '' });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [profileLoading, setProfileLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  const plans = [
    {
      id: 'starter',
      name: 'Starter',
      price: '$19',
      interval: 'month',
      stripeLink: 'https://buy.stripe.com/3cI8wP3KOcP69p15XtcEw02',
      description: 'Perfect for getting started',
      features: [
        '50 product imports/month',
        'Basic support',
        'Email notifications',
        '1 user seat'
      ],
      cta: 'Get Started',
      isCurrent: user?.plan === 'STARTER'
    },
    {
      id: 'growth',
      name: 'Growth',
      price: '$49',
      interval: 'month',
      stripeLink: 'https://buy.stripe.com/4gM4gzgxA3ew7gTclRcEw03',
      description: 'For growing businesses',
      features: [
        '500 product imports/month',
        'Priority support',
        'API access',
        'Bulk operations',
        'Up to 5 users'
      ],
      cta: 'Choose Growth',
      isCurrent: user?.plan === 'GROWTH',
      isPopular: true
    },
    {
      id: 'agency',
      name: 'Agency',
      price: '$89',
      interval: 'month',
      stripeLink: 'https://buy.stripe.com/7sY9AT1CG6qI30D1HdcEw01',
      description: 'For agencies and high-volume users',
      features: [
        '2000 product imports/month',
        '24/7 priority support',
        'Advanced analytics',
        'Unlimited users',
        'Dedicated account manager'
      ],
      cta: 'Contact Sales',
      isCurrent: user?.plan === 'AGENCY'
    }
  ];

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      const response = await api.get('/api/auth/me');
      const userData = response.parsedBody;
      setUser(userData);
      setProfileForm({
        name: userData.name || '',
        email: userData.email || ''
      });
    } catch (error) {
      console.error('Failed to load user data:', error);
      toast.error('Failed to load account information');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setProfileLoading(true);

    try {
      await api.patch('/api/settings/profile', {
        name: profileForm.name
      });
      
      setUser(prev => ({ ...prev, name: profileForm.name }));
      toast.success('Profile updated successfully');
    } catch (error) {
      console.error('Profile update failed:', error);
      toast.error('Failed to update profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setPasswordLoading(true);

    try {
      await api.post('/api/auth/change-password', {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      });

      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      toast.success('Password changed successfully');
    } catch (error) {
      console.error('Password change failed:', error);
      toast.error(error.data?.error || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await api.post('/api/auth/logout');
    } catch (error) {
      console.error('Logout request failed:', error);
    } finally {
      // Clear token and redirect regardless of API response
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
  };

  const handleUpgradePlan = async (plan) => {
    if (plan.id === 'agency') {
      window.location.href = 'mailto:sales@autolistify.com';
      return;
    }
    
    // Redirect directly to Stripe Checkout
    window.location.href = plan.stripeLink;
  };

  const handleManageBilling = async () => {
    try {
      const response = await api.get('/api/payments/portal');
      
      if (response.parsedBody?.url) {
        window.location.href = response.parsedBody.url;
      }
    } catch (error) {
      console.error('Failed to access billing portal:', error);
      toast.error('Failed to access billing portal');
    }
  };

  if (loading) {
    return (
      <Box p={3}>
        <Typography>Loading account information...</Typography>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Account Settings
      </Typography>

      <Grid container spacing={3}>
        {/* Profile Section */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              avatar={<Person />}
              title="Profile Information"
              subheader="Update your account details"
            />
            <CardContent>
              <form onSubmit={handleProfileUpdate}>
                <Stack spacing={2}>
                  <TextField
                    label="Email"
                    value={profileForm.email}
                    disabled
                    fullWidth
                    helperText="Email cannot be changed"
                  />
                  <TextField
                    label="Name"
                    value={profileForm.name}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, name: e.target.value }))}
                    fullWidth
                    required
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={profileLoading}
                    fullWidth
                  >
                    {profileLoading ? 'Updating...' : 'Update Profile'}
                  </Button>
                </Stack>
              </form>
            </CardContent>
          </Card>
        </Grid>

        {/* Password Section */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              avatar={<Lock />}
              title="Change Password"
              subheader="Update your account password"
            />
            <CardContent>
              <form onSubmit={handlePasswordChange}>
                <Stack spacing={2}>
                  <TextField
                    label="Current Password"
                    type="password"
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                    fullWidth
                    required
                  />
                  <TextField
                    label="New Password"
                    type="password"
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                    fullWidth
                    required
                  />
                  <TextField
                    label="Confirm New Password"
                    type="password"
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    fullWidth
                    required
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={passwordLoading}
                    fullWidth
                  >
                    {passwordLoading ? 'Changing...' : 'Change Password'}
                  </Button>
                </Stack>
              </form>
            </CardContent>
          </Card>
        </Grid>

        {/* Plan & Billing Section */}
        <Grid item xs={12}>
          <Card>
            <CardHeader
              avatar={<CreditCard />}
              title="Plan & Billing"
              subheader="Manage your subscription and usage"
            />
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h6" gutterBottom>
                      Current Plan
                    </Typography>
                    <Chip 
                      label={user?.plan || 'Free'} 
                      color={user?.plan === 'FREE' ? 'default' : 'primary'}
                      size="large"
                    />
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h6" gutterBottom>
                      Credits Remaining
                    </Typography>
                    <Typography variant="h4" color="primary">
                      {user?.credits || 0}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h6" gutterBottom>
                      Actions
                    </Typography>
                    <Stack spacing={1}>
                      {(!user?.plan || user?.plan === 'FREE') ? (
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => handleUpgradePlan(plans[0])}
                          fullWidth
                        >
                          Upgrade to Pro
                        </Button>
                      ) : (
                        <Button
                          variant="outlined"
                          onClick={handleManageBilling}
                          fullWidth
                        >
                          Manage Billing
                        </Button>
                      )}
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Plans Section */}
        <Grid item xs={12}>
          <Grid container spacing={3}>
            {plans.map((plan) => (
              <Grid item xs={12} md={4} key={plan.id}>
                <Paper 
                  elevation={plan.isPopular ? 6 : 2}
                  sx={{ 
                    p: 3, 
                    height: '100%',
                    border: plan.isPopular ? '2px solid #1976d2' : '1px solid #e0e0e0',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  {plan.isPopular && (
                    <Chip 
                      label="Most Popular" 
                      color="primary" 
                      size="small"
                      sx={{ position: 'absolute', top: 10, right: 10 }}
                    />
                  )}
                  <Box flexGrow={1}>
                    <Typography variant="h5" gutterBottom>
                      {plan.name}
                    </Typography>
                    <Box display="flex" alignItems="baseline" mb={2}>
                      <Typography variant="h4">{plan.price}</Typography>
                      <Typography variant="body2" color="textSecondary" ml={1}>
                        /{plan.interval}
                      </Typography>
                    </Box>
                    <Typography color="textSecondary" paragraph>
                      {plan.description}
                    </Typography>
                    <Box mb={3}>
                      {plan.features.map((feature, i) => (
                        <Box key={i} display="flex" alignItems="center" mb={1}>
                          <CheckCircleOutlineIcon fontSize="small" color="primary" />
                          <Typography variant="body2" ml={1}>
                            {feature}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                  <Button
                    fullWidth
                    variant={plan.isCurrent ? 'outlined' : 'contained'}
                    color={plan.isCurrent ? 'primary' : 'primary'}
                    onClick={() => handleUpgradePlan(plan)}
                    disabled={plan.isCurrent}
                    size="large"
                    sx={{ mt: 'auto' }}
                  >
                    {plan.isCurrent ? 'Current Plan' : plan.cta}
                  </Button>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* Logout Section */}
        <Grid item xs={12}>
          <Card>
            <CardHeader
              avatar={<ExitToApp />}
              title="Account Actions"
              subheader="Sign out of your account"
            />
            <CardContent>
              <Button
                variant="outlined"
                color="error"
                onClick={handleLogout}
                startIcon={<ExitToApp />}
              >
                Sign Out
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AccountPage;
